"use client";

import { useEffect, useRef, useState } from "react";
import { useRouter, useSearchParams, usePathname } from "next/navigation";

/** 서비스 전반 제품 카테고리 (6대분류, 2026-04-24 확정) */
export const CATEGORIES = [
  "음료",
  "베이커리",
  "디저트",
  "재료·원료",
  "유제품",
  "포장·소모품",
] as const;

export type CategoryValue = (typeof CATEGORIES)[number];

interface Props {
  /** true 면 페이지 URL 의 ?category= 쿼리로 상태 저장·복원.
   *  false 면 value/onChange props 로 외부 제어 (이 경우 두 prop 필수). */
  useUrlQuery?: boolean;
  value?: string;
  onChange?: (v: string) => void;
  /** 우측 카운트 표시용 (API 응답 `categories` 맵) */
  counts?: Record<string, number>;
  /** "전체" 칩 라벨 (default "전체") */
  allLabel?: string;
  className?: string;
}

export default function CategoryFilterChips({
  useUrlQuery = true,
  value,
  onChange,
  counts,
  allLabel = "전체",
  className = "",
}: Props) {
  const router = useRouter();
  const pathname = usePathname();
  const params = useSearchParams();

  const current = useUrlQuery ? (params.get("category") ?? "") : (value ?? "");

  const select = (c: string) => {
    if (useUrlQuery) {
      const next = new URLSearchParams(params.toString());
      if (c) next.set("category", c);
      else next.delete("category");
      const qs = next.toString();
      router.replace(qs ? `${pathname}?${qs}` : pathname);
    } else {
      onChange?.(c);
    }
  };

  const baseCls =
    "px-3.5 py-2 rounded-full text-xs font-semibold whitespace-nowrap transition-colors border";
  const activeCls = "bg-primary text-bg border-primary";
  const inactiveCls = "border-border text-text-sub hover:text-text hover:border-primary/30";

  // ─── 스크롤 인터랙션 ────────────────────────────
  // 1) 마우스 휠(수직) → 수평 스크롤 변환
  // 2) 클릭 + 드래그로 좌우 스와이프 (PC 에서 직관적)
  // 3) 오른쪽에 더 내용 있으면 페이드 그라디언트로 힌트
  const scrollRef = useRef<HTMLDivElement>(null);
  const dragState = useRef({ startX: 0, scrollLeft: 0, moved: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [hasMoreRight, setHasMoreRight] = useState(false);

  const updateFade = () => {
    const el = scrollRef.current;
    if (!el) return;
    setHasMoreRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  useEffect(() => {
    updateFade();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateFade, { passive: true });
    window.addEventListener("resize", updateFade);
    return () => {
      el.removeEventListener("scroll", updateFade);
      window.removeEventListener("resize", updateFade);
    };
  }, []);

  const onWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    const el = scrollRef.current;
    if (!el || e.deltaX !== 0 || e.deltaY === 0) return;
    el.scrollLeft += e.deltaY;
    e.preventDefault();
  };

  const onMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = scrollRef.current;
    if (!el) return;
    dragState.current = { startX: e.pageX, scrollLeft: el.scrollLeft, moved: 0 };
    setIsDragging(true);
  };
  const onMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDragging) return;
    const el = scrollRef.current;
    if (!el) return;
    const walk = e.pageX - dragState.current.startX;
    dragState.current.moved = Math.max(dragState.current.moved, Math.abs(walk));
    el.scrollLeft = dragState.current.scrollLeft - walk;
  };
  const onMouseUp = () => setIsDragging(false);

  // 드래그로 5px 이상 이동했으면 click 을 취소 (선택 버그 방지)
  const guardedSelect = (value: string, e: React.MouseEvent) => {
    if (dragState.current.moved > 5) {
      e.preventDefault();
      return;
    }
    select(value);
  };

  return (
    <div className="relative">
      <div
        ref={scrollRef}
        onWheel={onWheel}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
        className={`flex gap-2 overflow-x-auto hide-scrollbar select-none ${
          isDragging ? "cursor-grabbing" : "cursor-grab"
        } ${className}`}
      >
        <button
          onClick={(e) => guardedSelect("", e)}
          className={`${baseCls} ${!current ? activeCls : inactiveCls}`}
        >
          {allLabel}
        </button>
        {CATEGORIES.map((c) => {
          const active = current === c;
          const cnt = counts?.[c];
          return (
            <button
              key={c}
              onClick={(e) => guardedSelect(c, e)}
              className={`${baseCls} ${active ? activeCls : inactiveCls}`}
            >
              {c}
              {cnt !== undefined && (
                <span className={`ml-1 ${active ? "opacity-90" : "opacity-60"}`}>
                  ({cnt})
                </span>
              )}
            </button>
          );
        })}
      </div>
      {hasMoreRight && (
        <div
          className="pointer-events-none absolute right-0 top-0 bottom-0 w-10 bg-gradient-to-l from-bg to-transparent"
          aria-hidden
        />
      )}
    </div>
  );
}
